/**
 * UTC offsets of U.S. time zones without Intl.
 *
 * Why: on Cloudflare Workers the first `Intl.DateTimeFormat` for a zone costs milliseconds of CPU
 * (the free plan allows 10 per request) and every cold isolate pays it again. Every zone the NWS
 * names inside Weather Fusion's coverage follows one rule (since 2007): daylight time starts at
 * 02:00 local standard time on the second Sunday of March and ends at 02:00 local daylight time on
 * the first Sunday of November — or never, in Arizona, Hawaii and the territories. That is a few
 * additions, so these zones are answered here and anything else falls back to Intl (util.ts).
 *
 * `tests/tzfast.test.ts` compares every zone below with Intl around each transition and
 * through the year, 2024–2035, and the clock strings below with Intl's.
 */

const HOUR = 3_600_000

/** Standard-time offset (hours) of each zone, and whether it observes daylight time. */
const ZONES: Record<string, [number, boolean]> = {
  'America/New_York': [-5, true],
  'America/Detroit': [-5, true],
  'America/Kentucky/Louisville': [-5, true],
  'America/Kentucky/Monticello': [-5, true],
  'America/Indiana/Indianapolis': [-5, true],
  'America/Indiana/Vincennes': [-5, true],
  'America/Indiana/Winamac': [-5, true],
  'America/Indiana/Marengo': [-5, true],
  'America/Indiana/Petersburg': [-5, true],
  'America/Indiana/Vevay': [-5, true],
  'America/Chicago': [-6, true],
  'America/Indiana/Knox': [-6, true],
  'America/Indiana/Tell_City': [-6, true],
  'America/Menominee': [-6, true],
  'America/North_Dakota/Center': [-6, true],
  'America/North_Dakota/New_Salem': [-6, true],
  'America/North_Dakota/Beulah': [-6, true],
  'America/Denver': [-7, true],
  'America/Boise': [-7, true],
  'America/Phoenix': [-7, false],
  'America/Los_Angeles': [-8, true],
  'America/Anchorage': [-9, true],
  'America/Juneau': [-9, true],
  'America/Sitka': [-9, true],
  'America/Yakutat': [-9, true],
  'America/Nome': [-9, true],
  'America/Metlakatla': [-9, true],
  'America/Adak': [-10, true],
  'Pacific/Honolulu': [-10, false],
  'America/Puerto_Rico': [-4, false],
  'America/St_Thomas': [-4, false],
  'Pacific/Guam': [10, false],
  'Pacific/Saipan': [10, false],
  'Pacific/Pago_Pago': [-11, false],
}

export const FAST_ZONES = Object.freeze(Object.keys(ZONES))

/** True for a zone this module can answer (so callers can skip Intl to validate it). */
export const isFastZone = (timeZone: string | null | undefined): boolean => !!timeZone && timeZone in ZONES

const dow = (year: number, month: number, day: number) => new Date(Date.UTC(year, month, day)).getUTCDay()

const transitions = new Map<number, [number, number]>()

/** [start, end) of daylight time in `year` as UTC instants for a zone at standard offset `std` hours. */
function dstRange(year: number, std: number): [number, number] {
  const key = year * 100 + std
  let t = transitions.get(key)
  if (!t) {
    const secondSundayMarch = 1 + ((7 - dow(year, 2, 1)) % 7) + 7
    const firstSundayNovember = 1 + ((7 - dow(year, 10, 1)) % 7)
    t = [Date.UTC(year, 2, secondSundayMarch, 2) - std * HOUR, Date.UTC(year, 10, firstSundayNovember, 2) - (std + 1) * HOUR]
    transitions.set(key, t)
  }
  return t
}

/** UTC offset (ms) of `timeZone` at instant `ms`, or null when this module does not know the zone. */
export function fastZoneOffset(ms: number, timeZone: string): number | null {
  const z = ZONES[timeZone]
  if (!z) return null
  const [std, dst] = z
  if (!dst) return std * HOUR
  const [start, end] = dstRange(new Date(ms).getUTCFullYear(), std)
  return (ms >= start && ms < end ? std + 1 : std) * HOUR
}

export interface LocalFields {
  year: number
  month: number
  day: number
  hour: number
  minute: number
}

/** Wall-clock fields of an instant in a U.S. zone, or null for a zone this module does not know. */
export function fastLocal(ms: number, timeZone: string): LocalFields | null {
  const off = fastZoneOffset(ms, timeZone)
  if (off == null) return null
  const d = new Date(ms + off)
  return { year: d.getUTCFullYear(), month: d.getUTCMonth() + 1, day: d.getUTCDate(), hour: d.getUTCHours(), minute: d.getUTCMinutes() }
}

const two = (n: number) => (n < 10 ? `0${n}` : String(n))

/**
 * "7 PM" · "7:22 PM" (12-hour) or "19" · "19:22" (24-hour): what Intl's en-US `hour`/`minute` formats give,
 * with `space` (an ordinary space by default) before AM/PM. Null for a zone this module does not know.
 */
export function fastClock(ms: number, timeZone: string, clock: 12 | 24, minutes: boolean, space = ' '): string | null {
  const f = fastLocal(ms, timeZone)
  if (!f) return null
  if (clock === 24) return minutes ? `${two(f.hour)}:${two(f.minute)}` : two(f.hour)
  const h = f.hour % 12 || 12
  return `${h}${minutes ? `:${two(f.minute)}` : ''}${space}${f.hour < 12 ? 'AM' : 'PM'}`
}

/** "YYYY-MM-DD" in the zone, or null for a zone this module does not know. */
export function fastDate(ms: number, timeZone: string): string | null {
  const f = fastLocal(ms, timeZone)
  return f ? `${f.year}-${two(f.month)}-${two(f.day)}` : null
}
